import Vue from 'vue';
import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';

interface DbSyncManagerState {
    loaded: boolean;
    loading: boolean;
    fatalError: boolean;
    runningConfigId: number | null;
    lastMeta: {
        page: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
    };
    documents: any[];
}

const state: DbSyncManagerState = {
    loading: false,
    loaded: false,
    fatalError: false,
    runningConfigId: null,
    lastMeta: {
        page: 0,
        pageSize: 0,
        totalCount: 0,
        totalPages: 0
    },
    documents: []
};

export const ITEMS_LIST = 'DB_SYNC_CONFIGS_LIST';
export const RUN_SYNC = 'DB_SYNC_RUN';
export const TOGGLE_ENABLED = 'DB_SYNC_TOGGLE_ENABLED';

function resolveConfigId(config: any): number | string {
    if (typeof config !== 'object') {
        return config;
    }

    const rawId = config.id;
    if (typeof rawId === 'number') {
        return rawId;
    }

    if (typeof rawId === 'string' && rawId.includes(':')) {
        const parts = rawId.split(':');
        return parseInt(parts[parts.length - 1], 10);
    }

    return rawId;
}

export const actions: ActionTree<DbSyncManagerState, RootState> = {
    loadData({ commit }, query) {
        const vm: any = (this as any)._vm;

        return new Promise((resolve, reject) => {
            commit('START_LOADING');

            const sortField = query.sortField || 'schema_code';
            const sortDirection = query.sortDirection || 'asc';

            vm.$db([
                vm.$q(ITEMS_LIST)
                    .raw('documents.list')
                    .action({
                        columns: {
                            id: 'cfg.id',
                            schema_code: 'cfg.schema_code',
                            direction: 'cfg.direction',
                            transport: 'cfg.transport',
                            sync_strategy: 'cfg.sync_strategy',
                            remote_table: 'cfg.remote_table',
                            enabled: 'cfg.enabled',
                            interval_minutes: 'cfg.interval_minutes',
                            last_run_at: 'cfg.last_run_at',
                            last_status: 'cfg.last_status',
                            created_at: 'cfg.created_at'
                        },
                        from: 's:db_sync_configs',
                        from_as: 'cfg',
                        page_size: 20,
                        page: parseInt(query.page, 10) || 1,
                        order: [
                            sortDirection === 'desc'
                                ? { $desc: [`cfg.${sortField}`] }
                                : { $asc: [`cfg.${sortField}`] }
                        ]
                    })
            ])
                .then((result: ServerResult) => {
                    commit('SET_ITEMS_LIST', result.getOperation(ITEMS_LIST).getContent());
                    commit('LOADED_SUCCESS');
                    resolve(null);
                })
                .catch(() => {
                    commit('LOADED_ERROR');
                    reject();
                });
        });
    },

    runSync({ commit, dispatch }, { config, query }) {
        const vm: any = (this as any)._vm;
        const configId = resolveConfigId(config);

        return new Promise((resolve, reject) => {
            commit('SET_RUNNING_CONFIG', configId);

            vm.$db([
                vm.$q(RUN_SYNC)
                    .raw('db_sync.run')
                    .action({ config_id: configId })
            ])
                .then((result: ServerResult) => {
                    const op = result.getOperation(RUN_SYNC);
                    commit('SET_RUNNING_CONFIG', null);

                    if (op.hasError()) {
                        const content = op.getContent() || {};
                        vm.$buefy.toast.open({
                            message: content.text || 'Ошибка синхронизации',
                            type: 'is-danger'
                        });
                        reject();
                        return;
                    }

                    vm.$buefy.toast.open({
                        message: 'Синхронизация завершена',
                        type: 'is-success'
                    });
                    // $route есть только у компонентов vue-router, у store._vm его нет —
                    // текущий query передаёт вызывающий компонент
                    dispatch('loadData', query || {});
                    resolve(op.getContent());
                })
                .catch((err: any) => {
                    commit('SET_RUNNING_CONFIG', null);
                    vm.$buefy.toast.open({
                        message: err?.message || 'Ошибка синхронизации',
                        type: 'is-danger'
                    });
                    reject(err);
                });
        });
    },

    toggleEnabled({ dispatch }, { document, query }) {
        const vm: any = (this as any)._vm;

        return new Promise((resolve, reject) => {
            vm.$db([
                vm.$q(TOGGLE_ENABLED)
                    .update(document.id, { enabled: !document.enabled })
            ])
                .then((result: ServerResult) => {
                    const op = result.getOperation(TOGGLE_ENABLED);
                    if (op.hasError()) {
                        reject();
                        return;
                    }
                    dispatch('loadData', query || {});
                    resolve(null);
                })
                .catch(reject);
        });
    }
};

export const mutations: MutationTree<DbSyncManagerState> = {
    START_LOADING(state) {
        state.loaded = false;
        state.loading = true;
        state.fatalError = false;
    },

    LOADED_SUCCESS(state) {
        state.loaded = true;
        state.loading = false;
        state.fatalError = false;
    },

    LOADED_ERROR(state) {
        state.loaded = true;
        state.loading = false;
        state.fatalError = true;
    },

    SET_RUNNING_CONFIG(state, configId) {
        state.runningConfigId = configId;
    },

    SET_ITEMS_LIST(state, data) {
        state.documents = data.documents || [];
        const meta = data.meta || {};
        state.lastMeta = {
            page: meta.page || 1,
            pageSize: meta.page_size || 20,
            totalCount: meta.total_count || 0,
            totalPages: meta.total_pages || 0
        };
    }
};

export const STORE_KEY = '$_db_sync_manager';

export const store = {
    namespaced: true,
    state,
    actions,
    mutations
};
